'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLocale, useTranslations } from 'next-intl';
import { sv, enGB } from 'date-fns/locale';

interface DatePickerProps {
  value?: Date;
  onChange: (date: Date) => void;
  disabled?: boolean;
  align?: 'start' | 'center' | 'end';
  showIcon?: boolean;
  grouped?: 'start' | 'end';
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  disabled = false,
  align = 'start',
  showIcon = true,
  grouped,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const t = useTranslations('calendar');
  const locale = useLocale();
  const dateLocale = locale === 'sv' ? sv : enGB;

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    // Preserve the existing time-of-day when only the day changes.
    if (value) {
      date.setHours(value.getHours());
      date.setMinutes(value.getMinutes());
    }
    onChange(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !showIcon && 'px-2.5 tabular-nums',
            grouped && 'justify-center rounded-none border-0 px-2 text-center shadow-none',
            grouped === 'start' && 'rounded-l-md',
            grouped === 'end' && 'rounded-r-md',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          {showIcon && <CalendarIcon className="mr-2 h-4 w-4" />}
          {value ? (
            format(value, locale === 'sv' ? 'yyyy-MM-dd' : 'LLL dd, y', {
              locale: dateLocale,
            })
          ) : (
            <span>{t('form.datePicker.pickDate')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          locale={dateLocale}
          mode="single"
          selected={value}
          defaultMonth={value}
          onSelect={handleSelect}
          showWeekNumber={true}
          initialFocus={true}
        />
      </PopoverContent>
    </Popover>
  );
}
